// src/prf/prfReview.js
// Everything the PRF review screen shows, as pure builders over rows the caller
// has already fetched. No React, no network — so the screen, a tool and a
// checker all get the same answers from the same code.
//
// The five sections, and the rule that governs each:
//
//   CURRENT COVERAGE   what the policy says today, priced through the shipped
//                      quote engine rather than re-derived. The stored
//                      liability is shown beside our figure so a disagreement
//                      is visible instead of hidden.
//   COVERAGE LEVEL     70/75/80/85/90 on the producer's OWN allocation, with
//                      the low-index-year tradeoff. NEVER an optimizer output —
//                      searching coverage put every candidate at 90% on all 61
//                      policies in the book, because at CY2026 rates 90%
//                      dominates on both axes at once. That is a property of
//                      RMA's rate and subsidy tables, not a finding about a
//                      farm, so it is presented as a comparison and the
//                      dominance is stated on its face.
//   INTERVAL HISTORY   all eleven intervals over RMA's published record.
//   SIMULATOR          price any (coverage, allocation) and compare to current.
//   OPTIMIZER          EXPERIMENTAL. See the note on optimizerSection().
//
// ⚠ "SEVERE" HERE MEANS LOW RAINFALL INDEX, NOT MEASURED FORAGE LOSS. Every
// severity figure on this screen is a statement about RMA's index for this
// grid. We cannot measure forage response at all today — docs/prf-risk-relevance-plan.md
// Step 0 establishes that on three independent datasets — so no label on this
// screen may imply an observed crop outcome.
//
// ⚠ FAIL CLOSED ON LIMITS. Without RMA's percent-of-value limits for a grid,
// nothing here calls an allocation legal and the optimizer produces nothing.
// A priced quote is still shown, because arithmetic on a hypothetical is not a
// claim that RMA would accept it — but it is labelled unverified.

const { paymentFactor, PRF_INTERVALS } = require("./prfIndexMethodology.js");
const { quotePrf } = require("./prfQuote.js");
const OPT = require("./prfOptimizer.js");
const ACT = require("./prfActuarial.js");

const LAB = OPT.LAB;
const COVERAGE_LEVELS = [0.70, 0.75, 0.80, 0.85, 0.90];
// RMA's published subsidy schedule for plan 13. The quote engine carries the
// same table and is the authority; this copy exists only to price the ladder's
// per-dollar terms without a quote per level.
const SUBSIDY = { 0.70: 0.59, 0.75: 0.59, 0.80: 0.55, 0.85: 0.55, 0.90: 0.51 };

const mean = (a) => (a.length ? a.reduce((s, x) => s + x, 0) / a.length : null);
const round2 = (x) => Math.round(x * 100) / 100;

// ── the policy, split into the blocks it is actually written as ─────
// One PRF policy can hold several (grid, acreage) blocks, and each carries its
// own allocation summing to 100%. Rolling them together would blend two
// independent elections into one that the grower never made.
function prfBlocks(intervalRows, coverageLevel, useOf = null) {
  const by = {};
  for (const r of intervalRows || []) {
    const grid = Number(r.grid_id);
    const acres = Number(r.insured_acres) || 0;
    if (!grid) continue;
    const use = useOf ? (useOf(r) || null) : null;
    // ⚠ SHARE IS PART OF THE BLOCK. CP 26-RI-PRF §2(b) counts the share in the
    // unit an election is made for, and the AIP prices each line at its own share
    // (M13 P11 f[42]). Rows that STATE a share keep it; rows that do not (every
    // row loaded before insured_share existed) read 100% until the block is
    // reconciled against its own liability (ACT.reconcileLineFactors).
    const shareStated = r.insured_share != null && r.insured_share !== "" && Number(r.insured_share) > 0;
    const share = shareStated ? Number(r.insured_share) : 1;
    const key = `${grid}|${acres}${use ? "|" + use : ""}${shareStated && share !== 1 ? "|s" + share : ""}`;
    (by[key] ||= {
      key, grid, county: r.county || null, acres, intended_use: use,
      productivity_factor: r.productivity_factor == null ? null : Number(r.productivity_factor),
      share, share_stated: shareStated,
      coverage_level: coverageLevel == null ? null : Number(coverageLevel),
      allocation: {}, stored_liability: 0, stored_premium: 0, rows: 0, lines: [],
    });
    const b = by[key];
    b.lines.push({ interval_code: r.interval_code, acres, percent_value: Number(r.percent_value || 0),
                   liability: r.liability == null ? null : Number(r.liability),
                   premium: r.premium == null ? null : Number(r.premium),
                   productivity_factor: r.productivity_factor == null ? null : Number(r.productivity_factor) });
    b.allocation[r.interval_code] = (b.allocation[r.interval_code] || 0) + Number(r.percent_value || 0);
    b.stored_liability += Number(r.liability || 0);
    b.stored_premium += Number(r.premium || 0);
    b.rows++;
    if (b.productivity_factor == null && r.productivity_factor != null) b.productivity_factor = Number(r.productivity_factor);
    // Two lines of one block stating different productivity factors is a load
    // problem, REPORTED rather than averaged.
    if (r.productivity_factor != null && b.productivity_factor != null &&
        Math.abs(Number(r.productivity_factor) - b.productivity_factor) > 1e-9) b.pf_mixed = true;
  }
  const out = Object.values(by).sort((a, b) => b.stored_liability - a.stored_liability || a.grid - b.grid);
  for (const b of out) {
    b.intervals = Object.keys(b.allocation).filter((l) => b.allocation[l] > 0)
      .sort((x, y) => LAB.indexOf(x) - LAB.indexOf(y));
    b.label = b.intervals.map((l) => `${l} ${Math.round(b.allocation[l] * 100)}`).join(" / ");
    // The elections must sum to 100%. A block that does not is a load problem,
    // and it is REPORTED rather than normalised — normalising would silently
    // invent an election the grower never made.
    b.allocation_total = round2(b.intervals.reduce((s, l) => s + b.allocation[l], 0));
    b.allocation_sums = Math.abs(b.allocation_total - 1) < 0.005;
  }
  return out;
}

// ── reference data, indexed ─────────────────────────────────────────
function indexSeries(indexRows) {
  const series = {}, yearSet = new Set();
  for (const r of indexRows || []) {
    const code = Number(r.interval_code);
    const lab = Number.isFinite(code) ? LAB[code - 625] : r.interval_code;
    if (!lab || !LAB.includes(lab)) continue;
    const y = Number(r.year);
    if (!Number.isFinite(y)) continue;
    const v = Number(r.index_value);
    if (!Number.isFinite(v)) continue;
    (series[lab] ||= {})[y] = v;
    yearSet.add(y);
  }
  return { series, years: [...yearSet].sort((a, b) => a - b) };
}

// ── THE YEAR RMA HAS ALREADY SETTLED ───────────────────────────────
//
// RMA's published history file is republished once a crop year and restates
// every past year against an advancing normal (mean 1948..Y-2), so a vintage
// STRUCTURALLY cannot contain the year that just settled. The CY2026 file runs
// 1948-2024 — and 2025 is the year a grower actually remembers, because RMA paid
// on it. Leaving the record at 2024 answered a question nobody asked.
//
// `prf_grid_indices.final_index_value` is that settled index: the number RMA
// computed on the settling year's own normal and paid against. For a settled
// year it is the MORE authoritative figure, not the less — the restated vintage
// is a modelling convenience, while this is what determined money.
//
// ⚠ THE TWO SIT ON DIFFERENT NORMALS AND THE DIFFERENCE IS MEASURED, NOT
// ASSUMED. Across 440 overlapping (grid, year, interval) triples on five grids,
// 2017-2024, only 17 agree. The gap is a clean per-interval RESCALING rather
// than noise — history / final is constant within an interval across eight years
// at a coefficient of variation of 0.2-1.0%, mean 0.50%, mean ratios inside 1.6%
// of one — which is exactly what an advancing normal produces. So the years are
// carried with their basis NAMED rather than silently blended, and three rules
// keep the blend honest:
//
//   1. APPEND ONLY, NEVER OVERWRITE. A settled year is added only if it is
//      strictly newer than every year the vintage carries. Restating a year the
//      vintage already covers would move figures inside the replay window for no
//      gain, which is the thing worth refusing.
//   2. WHOLE YEARS ONLY. Every one of the eleven intervals must be final.
//      CY2026 has 4 of 11 settled today; half a year would take a full year's
//      premium against a partial indemnity, which is the same defect
//      simulatePrf's own skip rule exists to prevent.
//   3. THE YEARS ARE RETURNED SO THE SCREEN CAN SAY WHICH THEY ARE. A basis
//      difference the reader cannot see is the one that misleads.
//
// ⚠ AND THE UNITS DIFFER: finals are stored as PERCENT (156.5) and the history
// as a FRACTION (1.565). Merging without dividing is wrong by 100x and would be
// obvious on screen — which is the good kind of wrong, but the checker pins it.
function mergeSettledYears(series, years, finalRows) {
  const maxVintage = years.length ? Math.max(...years) : -Infinity;
  const byYear = {};
  for (const r of finalRows || []) {
    const v = Number(r.final_index_value);
    const lab = String(r.interval_code || "").toUpperCase();
    const y = Number(r.crop_year);
    if (!Number.isFinite(v) || !Number.isFinite(y) || !LAB.includes(lab)) continue;
    if (!(y > maxVintage)) continue;                 // rule 1 — append only
    (byYear[y] ||= {})[lab] = v / 100;               // percent -> fraction
  }
  const added = [];
  const out = { ...series };
  for (const y of Object.keys(byYear).map(Number).sort((a, b) => a - b)) {
    const got = byYear[y];
    if (LAB.some((l) => got[l] == null)) continue;   // rule 2 — whole years only
    for (const l of LAB) { (out[l] ||= {}); out[l][y] = got[l]; }
    added.push(y);
  }
  return { series: out, years: [...years, ...added].sort((a, b) => a - b), settled_years: added };
}

function ratesFor(rateRows, coverage, narrow = null) {
  const out = {};
  const ambiguous = [];
  for (const l of LAB) {
    let hits = (rateRows || []).filter((r) =>
      r.interval_code === l && Math.abs(Number(r.coverage_level) - coverage) < 1e-9);
    // Narrow by whatever the caller can state. Each of these is part of RMA's
    // own key, so narrowing can only ever remove rows that describe different
    // coverage — never the right one.
    if (narrow?.typeCode) {
      const t = hits.filter((r) => r.type_code == null || String(r.type_code) === String(narrow.typeCode));
      if (t.length) hits = t;
    }
    if (narrow?.countyCode) {
      const c = hits.filter((r) => r.county_code == null || String(r.county_code) === String(narrow.countyCode));
      if (c.length) hits = c;
    }
    // ⚠ THE PRACTICE CODE IS PER INTERVAL ON CY2027. RMA encodes the band and
    // the interval in ONE number — band 425 plus MAYJUN's position is 429 — so
    // a single string cannot narrow a whole election on that year. A FUNCTION
    // is accepted for exactly that: `(interval) => code`. The string path is
    // untouched, so every existing caller behaves identically.
    const prac = typeof narrow?.practiceCode === "function"
      ? narrow.practiceCode(l) : narrow?.practiceCode;
    if (prac) {
      const pr = hits.filter((r) => r.practice_code == null || String(r.practice_code) === String(prac));
      if (pr.length) hits = pr;
    }
    const vals = [...new Set(hits.map((r) => Number(r.base_rate)))];
    if (vals.length === 1) out[l] = vals[0];
    else if (vals.length > 1) { out[l] = null; ambiguous.push(l); }
    else out[l] = null;
  }
  // Reported, never silent: a surface can say WHY an interval could not price.
  Object.defineProperty(out, "__ambiguous", { value: ambiguous, enumerable: false });
  return out;
}

// RMA's percent-of-value rule for this grid, or null. Null is fail-closed: the
// caller must not call anything legal without it.
function limitsFor(limitRows, grid, cropYear) {
  const out = {};
  for (const r of limitRows || []) {
    if (Number(r.grid_id) !== Number(grid)) continue;
    if (cropYear != null && Number(r.crop_year) !== Number(cropYear)) continue;
    out[r.interval_code] = { min: Number(r.min_percent), max: Number(r.max_percent) };
  }
  return LAB.every((l) => out[l]) ? out : null;
}

// ── is this allocation one RMA would accept? ───────────────────────
// Returns {legal:true} / {legal:false, reasons:[...]} / {legal:null} when the
// limits are unverified — three states, because "we cannot check" is not "no".
function checkAllocation(alloc, limits) {
  const used = Object.keys(alloc || {}).filter((l) => alloc[l] > 0);
  const total = round2(used.reduce((s, l) => s + alloc[l], 0));
  const reasons = [];
  if (Math.abs(total - 1) > 0.005) reasons.push(`allocation totals ${Math.round(total * 100)}%, not 100%`);
  if (used.length < 2) reasons.push("RMA requires at least two intervals");
  if (!limits) return { legal: null, status: "RMA_LIMITS_UNVERIFIED", reasons, total };
  for (const l of used) {
    if (alloc[l] < limits[l].min - 1e-9) reasons.push(`${l} ${Math.round(alloc[l] * 100)}% is below the ${Math.round(limits[l].min * 100)}% minimum`);
    if (alloc[l] > limits[l].max + 1e-9) reasons.push(`${l} ${Math.round(alloc[l] * 100)}% is above the ${Math.round(limits[l].max * 100)}% maximum`);
  }
  return { legal: reasons.length === 0, status: reasons.length ? "INVALID" : "OK", reasons, total };
}

// ── price + replay one (coverage, allocation) ───────────────────────
// The single path from an election to a number. Everything on the screen —
// current, every ladder rung, every simulated change, every optimizer
// candidate — comes through here, so two sections cannot disagree.
function priceAndReplay(block, alloc, coverage, ctx) {
  const rates = ratesFor(ctx.rateRows, coverage, {
    typeCode: ctx.typeCode || null, countyCode: ctx.countyCode || null,
    practiceCode: ctx.practiceCode || null });
  const used = Object.keys(alloc).filter((l) => alloc[l] > 0);
  const missingRate = used.filter((l) => rates[l] == null);
  const ambiguousRate = (rates.__ambiguous || []).filter((l) => used.includes(l));
  const missingIndex = used.filter((l) => !ctx.series[l]);
  if (ambiguousRate.length) return { error: "RATE_AMBIGUOUS", intervals: ambiguousRate };
  if (missingRate.length) return { error: "NO_RATE", intervals: missingRate };
  let quote = null;
  try {
    quote = quotePrf({
      countyBaseValue: ctx.cbv, coverageLevel: coverage,
      productivityFactor: block.productivity_factor, acres: block.acres,
      share: block.share == null ? 1 : Number(block.share),
      allocation: alloc, rates, rules: ctx.limits || undefined,
      bfrBonus: Number(ctx.bfrBonus) || 0,
    });
  } catch (e) { return { error: "QUOTE_REFUSED", message: e.message }; }

  const years = ctx.years;
  const yearly = years.map((y) => used.reduce((s, l) => {
    const idx = ctx.series[l]?.[y];
    return s + (Number.isFinite(idx) ? alloc[l] * paymentFactor(coverage, idx) : 0);
  }, 0));
  const producerPerDollar = used.reduce((s, l) => s + alloc[l] * rates[l], 0) * (1 - SUBSIDY[coverage] - (Number(ctx.bfrBonus) || 0));
  const indem = yearly.map((f) => f * quote.total_protection);
  const net = indem.map((v) => v - quote.producer_premium);

  return {
    coverage, allocation: { ...alloc },
    label: used.sort((a, b) => LAB.indexOf(a) - LAB.indexOf(b))
      .map((l) => `${l} ${Math.round(alloc[l] * 100)}`).join(" / "),
    protection: quote.total_protection,
    total_premium: quote.total_premium,
    subsidy: quote.total_subsidy,
    producer_premium: quote.producer_premium,
    lines: quote.lines,
    years, yearly, indemnity_by_year: indem, net_by_year: net,
    avg_factor: mean(yearly), producer_per_dollar: producerPerDollar,
    avg_indemnity: mean(indem), avg_net: mean(net),
    pay_freq: yearly.filter((v) => v > 0).length / (yearly.length || 1),
    worst_net: net.length ? Math.min(...net) : null,
    best_net: net.length ? Math.max(...net) : null,
    missing_index: missingIndex,
    legality: checkAllocation(alloc, ctx.limits),
  };
}

// The pricing context for ONE block. Extracted so `buildBlockReview` and
// `policyOverview` share it rather than each assembling its own — two copies of
// this is how the whole-policy page and the per-grid page come to disagree about
// a county base value, which is the disagreement a reader has no way to resolve.
function blockContext(block, refs, cbvRes, series, years) {
  const cbvForPricing = cbvRes?.ok ? cbvRes.cbv : refs.cbv;
  const stage1 = OPT.stage1();
  let severity = null;
  try { severity = OPT.severityByYear(series, years, stage1.core); } catch (_) { severity = null; }
  // `severity` is returned as well as being put on the ctx, because
  // buildBlockReview still reads it directly for `severity_available` /
  // `severity_counts` / `severity_years`. Moving the computation in here without
  // handing it back left those three reading a name that no longer existed —
  // "severity is not defined" at the first call, and the whole review screen
  // rendered as a header over empty space.
  return {
    cbvForPricing, severity,
    ctx: {
      series, years, stage1, severity,
      severityDefId: "MEAN_CORE_P20",
      cbv: cbvForPricing, rateRows: refs.rateRows,
      // ⚠ A GRID WITH NO POLICY BEHIND IT SOLVES NOTHING, AND WITHOUT A COUNTY
      // THE RATE LOOKUP IS AMBIGUOUS. `resolveCbv` works backwards from the
      // AIP's own stored liability, so for a grid this customer does not
      // insure there is no liability to work from and `cbvRes` comes back
      // null — measured on grid 27509, which spans FOUR counties, where the
      // whole review then priced nothing (RATE_AMBIGUOUS) and the coverage
      // ladder and loss ratio came back empty.
      //
      // So the caller may STATE the county, the type and the county base
      // value where a human has picked them. A solved value always wins: for
      // a policy block these three are undefined and nothing changes.
      typeCode: cbvRes?.type_code || refs.typeCode || null,
      countyCode: cbvRes?.county || refs.countyCode || null,
      practiceCode: null,
      limits: limitsFor(refs.limitRows, block.grid, refs.cropYear),
      sourceCropYear: refs.sourceCropYear ?? null,
      // Beginning farmer and rancher points for the year being priced
      // (MGR-25-006). The caller reads them off the billed subsidy for the
      // policy year; 0 for anyone who is not a BFR.
      bfrBonus: Number(refs.bfrBonus) || 0,
    },
  };
}

// The block as the AIP actually wrote it. When resolveCbv had to read the
// share or productivity factor off the line liabilities (the share was never
// stored), every later price — current, ladder, simulator, renewal — uses the
// reconciled figures, and the block says where they came from.
function effectiveBlock(block, cbvRes) {
  const lf = cbvRes && cbvRes.line_factor;
  if (!lf || !(lf.status === "DERIVED_SHARE" || lf.status === "DERIVED_PF")) return block;
  return { ...block, share: lf.share, productivity_factor: lf.productivity_factor, line_factor: lf };
}

// ── THE WHOLE POLICY, NOT ONE GRID AT A TIME ───────────────────────
//
// A farm's PRF is one policy that can carry several blocks — a grid x acreage x
// intended-use cell each. The review screen made you pick one from a dropdown,
// so the only way to learn what the POLICY costs or protects was to visit each
// block and add them up by hand. Worse, nothing on screen said how many blocks
// there were, so a farm with four looked exactly like a farm with one.
//
// This prices EVERY block through the same `priceAndReplay` the single-block
// page uses, and totals them.
//
// ⚠ WHAT TOTALS AND WHAT MUST NOT. Acres, protection, premium and indemnity are
// additive across blocks and are summed. **Coverage level, productivity factor
// and county base value are NOT** — they are per-block elections, and a farm can
// genuinely carry 85% on one grid and 90% on another. Averaging them would
// invent a number that appears on no statement, which is this repo's oldest
// rule. Where the blocks agree the shared value is reported; where they differ
// the total says "varies" and the per-block rows carry the truth.
//
// ⚠ AND A BLOCK THAT CANNOT PRICE IS LISTED WITH ITS REASON, NEVER DROPPED. A
// policy total quietly missing a grid is the acreage-exclusion failure the New
// Quote screen already guards: it reads as a smaller farm rather than as an
// incomplete read.
// ⚠ AN IN-FORCE POLICY SHOWS WHAT IT WAS BILLED. `billed` is the policy's own
// prf_policies totals. Our re-pricing stays beside it as the check, but the
// figure a farmer reads as "what I pay" is the AIP's, so a gap in our pricing
// (BFR before MGR-25-006 was modelled: $887 shown against $616 billed on
// MP-0930254) can never again put a premium on screen that contradicts the bill.
function policyOverview(blocks, refsFor, billed = null) {
  const rows = [];
  for (const block of blocks || []) {
    const refs = refsFor(block) || null;
    // ⚠ AN UNPRICEABLE BLOCK STILL CARRIES STORED LIABILITY, AND OMITTING IT CRASHED
    // THE PAGE. This row was pushed WITHOUT `stored`, while `totals.stored_liability`
    // reduces over EVERY row (the priced totals reduce over `live`, so only these two
    // lines reach it) — so one grid with no county base values loaded threw
    // "Cannot read properties of undefined (reading 'liability')" out of
    // policyOverview and took the whole PRF Coverage page down with it, as a blank
    // screen rather than a degraded card.
    //
    // Found by this module's own checker on its first run, not on a customer's page.
    // Carrying the block's stored figures is also the CORRECT answer, not just the
    // safe one: `stored_liability` exists to be compared against the AIP's own total
    // ("the AIP's stored liability across these blocks is $495,013, which agrees with
    // ours"), and silently dropping a block from one side of that comparison would
    // make it disagree for a reason that has nothing to do with pricing.
    if (!refs) {
      rows.push({ block, ok: false, reason: "NO_REFS", says: "No actuarials loaded for this grid.",
                  county: block.county || null, county_code: null,
                  stored: { liability: block.stored_liability, premium: block.stored_premium } });
      continue;
    }
    const _iv = indexSeries(refs.indexRows);
    const { series, years } = mergeSettledYears(_iv.series, _iv.years, refs.settledRows || []);
    let cbvRes = null;
    if (refs.cbvIndex) {
      try {
        cbvRes = ACT.resolveCbv(
          { ...block, stored_liability_for_solve: refs.solveLiability ?? null,
            solve_percent: refs.solvePercent ?? null },
          { cropYear: refs.cropYear, policyNumber: refs.policyNumber, stateCode: refs.stateCode,
            cbvIndex: refs.cbvIndex, fpiIndex: refs.fpiIndex || null, tolerance: 0.51,
            blockUse: block.intended_use || null, blockIntervals: block.intervals });
      } catch (_) { cbvRes = null; }
    }
    const eff = effectiveBlock(block, cbvRes);
    const { ctx, cbvForPricing } = blockContext(eff, refs, cbvRes, series, years);
    const priced = priceAndReplay(eff, eff.allocation, eff.coverage_level, ctx);
    rows.push({
      block: eff, ok: !priced.error,
      reason: priced.error || null,
      says: priced.error ? (priced.message || `Cannot price: ${priced.error}${priced.intervals ? " (" + priced.intervals.join(", ") + ")" : ""}`) : null,
      cbv: cbvForPricing ?? null,
      cbv_source: cbvRes?.source || null,
      // ⚠ A COUNTY CODE IS NOT A COUNTY NAME, AND THIS FIELD HELD BOTH.
      //
      // `cbvRes.county` is the county CODE the rate was drawn from — line ~305 of
      // this file calls the same value `countyCode` — while `block.county` is the
      // NAME the policy states. Preferring the code put "113" in a column headed
      // COUNTY on Diamond D Farming's PRF page, and into the policy header, which
      // read "113, CLARE, OSCEOLA". Kevin, 2026-09-07: "PRF coverage is showing
      // county names as a number, should be county name".
      //
      // Only MISSAUKEE was affected on that policy, and the reason is worth keeping:
      // the other two blocks resolved no CBV, so they fell through to the name and
      // looked right. A field that is a name when a lookup fails and a code when it
      // succeeds is wrong in the direction nobody checks.
      //
      // ⚠ AND THERE IS NO CODE→NAME PATH FOR MICHIGAN TO FALL BACK ON.
      // FIPS_TO_NAME_GLOBAL in App.jsx is NEW YORK ONLY — every key begins "36" —
      // and STATE_FIPS carries NY/PA/MD/OH/IN and no MI. So translating the code
      // was never an option here; the name the AIP already stated is the answer.
      //
      // They are two fields now. `county` is what a human reads; `county_code` is
      // which county the RATE came from, kept because a grid can span counties and
      // that distinction is real — it is just not a label.
      county: block.county || null,
      county_code: cbvRes?.county || null,
      intended_use: cbvRes?.use || block.intended_use || null,
      record_years: years.length,
      priced: priced.error ? null : priced,
      stored: { liability: block.stored_liability, premium: block.stored_premium },
    });
  }

  const live = rows.filter((r) => r.ok);
  const one = (get) => {
    const vals = [...new Set(live.map(get).filter((v) => v != null))];
    return vals.length === 1 ? vals[0] : null;      // null == "varies", never an average
  };
  const sum = (get) => live.reduce((s, r) => s + (Number(get(r)) || 0), 0);
  const bill = billedTotals(billed);
  const priced = {
    protection: sum((r) => r.priced.protection),
    total_premium: sum((r) => r.priced.total_premium),
    subsidy: sum((r) => r.priced.subsidy),
    producer_premium: sum((r) => r.priced.producer_premium),
  };
  const complete = rows.length > 0 && live.length === rows.length;
  return {
    rows,
    // What the AIP billed, and whether our re-pricing reproduces it. `shown` is
    // the figure to print: the bill where there is one, ours otherwise.
    billed: bill,
    billed_check: bill ? {
      complete,
      agrees: complete && ["protection", "total_premium", "subsidy", "producer_premium"]
        .every((k) => bill[k] == null || Math.abs(bill[k] - priced[k]) < 1),
      gaps: Object.fromEntries(["protection", "total_premium", "subsidy", "producer_premium"]
        .filter((k) => bill[k] != null).map((k) => [k, priced[k] - bill[k]])),
    } : null,
    shown: {
      protection: bill?.protection ?? priced.protection,
      total_premium: bill?.total_premium ?? priced.total_premium,
      subsidy: bill?.subsidy ?? priced.subsidy,
      producer_premium: bill?.producer_premium ?? priced.producer_premium,
      source: bill ? "BILLED" : "PRICED",
    },
    blocks_total: rows.length,
    blocks_priced: live.length,
    // Every block that could not be priced, so a short total names itself.
    unpriceable: rows.filter((r) => !r.ok).map((r) => ({ grid: r.block.grid, acres: r.block.acres, says: r.says })),
    totals: {
      acres: sum((r) => r.block.acres),
      protection: sum((r) => r.priced.protection),
      total_premium: sum((r) => r.priced.total_premium),
      subsidy: sum((r) => r.priced.subsidy),
      producer_premium: sum((r) => r.priced.producer_premium),
      avg_indemnity: sum((r) => r.priced.avg_indemnity),
      avg_net: sum((r) => r.priced.avg_net),
      stored_liability: rows.reduce((s, r) => s + (Number(r.stored.liability) || 0), 0),
      stored_premium: rows.reduce((s, r) => s + (Number(r.stored.premium) || 0), 0),
      // Shared where shared, null where the blocks genuinely differ.
      coverage_level: one((r) => r.block.coverage_level),
      productivity_factor: one((r) => r.block.productivity_factor),
      county_base_value: one((r) => r.cbv),
      grids: [...new Set(rows.map((r) => r.block.grid))].sort((a, b) => a - b),
      counties: [...new Set(rows.map((r) => r.county).filter(Boolean))].sort(),
      uses: [...new Set(rows.map((r) => r.intended_use).filter(Boolean))].sort(),
    },
  };
}

// The billed figures, or null when the policy carries none. A zero or blank
// premium is "not billed yet", never a free policy.
function billedTotals(b) {
  if (!b) return null;
  const n = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
  const total = n(b.total_premium ?? b.totalPremium);
  if (!(total > 0)) return null;
  const subsidy = n(b.subsidy);
  const net = n(b.net_premium ?? b.netPremium) ?? (subsidy != null ? total - subsidy : null);
  return { protection: n(b.total_liability ?? b.totalLiability), total_premium: total,
           subsidy, producer_premium: net };
}

// ── COVERAGE LEVEL — a comparison, never an optimizer output ───────
function coverageSection(block, ctx) {
  const rows = [];
  for (const cov of COVERAGE_LEVELS) {
    const r = priceAndReplay(block, block.allocation, cov, ctx);
    if (r.error) { rows.push({ coverage: cov, available: false, reason: r.error }); continue; }
    const sev = ctx.severity ? OPT.severityMetrics(r.yearly, ctx.severity, ctx.years, r.protection, r.producer_premium) : null;
    rows.push({
      coverage: cov, available: true, is_current: Math.abs(cov - block.coverage_level) < 1e-9,
      protection: r.protection, producer_premium: r.producer_premium,
      avg_indemnity: r.avg_indemnity, avg_net: r.avg_net, pay_freq: r.pay_freq, worst_net: r.worst_net,
      severe: sev?.SEVERE || null, normal: sev?.NORMAL || null,
      // The replayed record, so a caller can ask "what was the loss ratio over the
      // last N years" without re-pricing. 
      years: r.years, indemnity_by_year: r.indemnity_by_year, net_by_year: r.net_by_year,
    });
  }
  // Dominance is REPORTED, not hidden. If 90% really does beat everything below
  // it on both return and steadiness, the agent should see that stated — and
  // see that it is a fact about RMA's rate table rather than about this farm.
  const live = rows.filter((r) => r.available);
  for (const r of live) {
    const lower = live.filter((o) => o.coverage < r.coverage);
    r.dominates_all_lower = lower.length > 0 &&
      lower.every((o) => r.avg_net >= o.avg_net - 1e-9 && r.pay_freq >= o.pay_freq - 1e-9);
  }
  const cur = live.find((r) => r.is_current) || null;
  for (const r of live) {
    r.vs_current = cur ? {
      additional_protection: r.protection - cur.protection,
      additional_producer_premium: r.producer_premium - cur.producer_premium,
      additional_avg_net: r.avg_net - cur.avg_net,
      additional_severe_net: (r.severe && cur.severe) ? r.severe.avg_net - cur.severe.avg_net : null,
      additional_normal_net: (r.normal && cur.normal) ? r.normal.avg_net - cur.normal.avg_net : null,
    } : null;
  }
  return { levels: rows, current_level: block.coverage_level, note: "COMPARISON_NOT_OPTIMIZED" };
}

// ── INTERVAL HISTORY — every interval, on RMA's published record ────
function intervalSection(block, ctx) {
  const cov = block.coverage_level;
  const out = [];
  for (const lab of LAB) {
    const s = ctx.series[lab];
    if (!s) { out.push({ interval: lab, available: false }); continue; }
    const ys = ctx.years.filter((y) => Number.isFinite(s[y]));
    const idx = ys.map((y) => s[y]);
    const factors = ys.map((y) => paymentFactor(cov, s[y]));
    const paidYears = ys.filter((y, i) => factors[i] > 0);
    const recentYs = ys.slice(-20), recentF = factors.slice(-20), recentI = idx.slice(-20);
    out.push({
      interval: lab, available: true, in_current: (block.allocation[lab] || 0) > 0,
      allocation: block.allocation[lab] || 0,
      relevance: ctx.stage1.classes[lab],
      n_years: ys.length, first_year: ys[0], last_year: ys[ys.length - 1],
      // ⚠ mean_index IS 1.000 FOR EVERY INTERVAL BY CONSTRUCTION. RMA publishes
      // the index as percent-of-normal against a normal derived from the same
      // record, so the full-history mean is an identity, not a measurement — it
      // is asserted as one in check-prf-index-history. It is kept because a
      // value that is NOT 1.000 means the history is truncated or mis-joined,
      // which makes it a useful integrity signal and a useless statistic. Any
      // surface showing it must say which of the two it is.
      mean_index: mean(idx), mean_index_is_identity: Math.abs((mean(idx) ?? 0) - 1) < 0.005,
      median_index: median(idx),
      min_index: Math.min(...idx), max_index: Math.max(...idx),
      // "Trigger frequency" is the share of years the index fell below this
      // coverage level — i.e. the share that would have PAID at the coverage
      // the policy carries. It moves with coverage and is meaningless without
      // it, so the level is carried alongside.
      trigger_freq: paidYears.length / ys.length, at_coverage: cov,
      // Payment behaviour: how big the payment was WHEN it paid, which is a
      // different question from how often — the same split the severity work
      // established.
      mean_factor_all: mean(factors),
      mean_factor_when_paid: paidYears.length ? mean(factors.filter((f) => f > 0)) : null,
      max_factor: Math.max(...factors),
      recent_trigger_freq: recentF.filter((f) => f > 0).length / (recentF.length || 1),
      recent_mean_index: mean(recentI), recent_n: recentF.length,
      // A drift is descriptive, and is stated as a difference rather than as a
      // trend — 20 years is one sample, not a direction.
      recent_vs_long_index: mean(recentI) - mean(idx),
      series: ys.map((y, i) => ({ year: y, index: idx[i], factor: factors[i] })),
    });
  }
  return { intervals: out, coverage_level: cov, record_years: ctx.years.length,
           source_crop_year: ctx.sourceCropYear };
}
function median(a) {
  if (!a.length) return null;
  const s = [...a].sort((x, y) => x - y), m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

// ── SIMULATOR — any structure, against current ─────────────────────
function simulate(block, alloc, coverage, ctx) {
  const cur = priceAndReplay(block, block.allocation, block.coverage_level, ctx);
  const sim = priceAndReplay(block, alloc, coverage, ctx);
  if (sim.error) return { error: sim.error, detail: sim };
  if (cur.error) return { error: cur.error, detail: cur };
  const h = OPT.headToHead(
    { yearly: sim.yearly, protection: sim.protection, producer_premium: sim.producer_premium, avg_factor: sim.avg_factor },
    { yearly: cur.yearly, protection: cur.protection, producer_premium: cur.producer_premium, avg_factor: cur.avg_factor });
  const bySev = ctx.severity ? OPT.headToHeadBySeverity(
    { yearly: sim.yearly, protection: sim.protection, producer_premium: sim.producer_premium },
    { yearly: cur.yearly, protection: cur.protection, producer_premium: cur.producer_premium },
    ctx.severity, ctx.years) : null;
  const sameCov = Number(coverage) === Number(block.coverage_level);
  const keys = new Set([...Object.keys(alloc || {}), ...Object.keys(block.allocation || {})]);
  const sameAlloc = [...keys].every((k) => Number((alloc || {})[k] || 0) === Number((block.allocation || {})[k] || 0));
  return { current: cur, proposed: sim, vs_current: h, vs_current_by_severity: bySev,
           is_unchanged: sameCov && sameAlloc };
}

// ── OPTIMIZER — EXPERIMENTAL, and it may decline ───────────────────
// ⚠ THIS SECTION IS NOT VALIDATED AND THE SCREEN MUST SAY SO. Three things are
// established and unresolved as of 2026-08-24:
//   · severe years are defined from the same core index the allocation is
//     optimised over, which is a circularity risk that has not been ruled out;
//   · the candidate concentrates hard — 79% of picks put >=70% in one interval
//     and 24 of 29 favour MAY-JUN — against 0 of 61 current allocations that
//     concentrate that far;
//   · whether that reflects real seasonal risk or an artifact of optimising one
//     objective has not been falsified across other climates.
// It is architected here so the product does not have to be rebuilt around it
// later, and gated so nobody mistakes it for advice.
//
// ⚠ NO_MATERIAL_IMPROVEMENT IS PRESERVED. On the live book it is the answer for
// 32 of 61 policies. A tool that always proposes a change proposes changes for
// its own sake.
function optimizerSection(block, ctx, opts = {}) {
  const retention = opts.retention ?? 0.90;
  const status = { experimental: true, methodology: "INDEX_SEVERITY_CONDITIONED",
    severity_definition: ctx.severityDefId || "MEAN_CORE_P20",
    severe_years_are: "LOW_INDEX_YEARS_NOT_MEASURED_FORAGE_LOSS" };
  if (!ctx.limits) return { ...status, status: "RMA_LIMITS_UNVERIFIED", candidate: null, ladder: null };
  if (!ctx.severity) return { ...status, status: "NO_SEVERITY", candidate: null, ladder: null };

  const admitted = OPT.admittedIntervals(ctx.stage1, OPT.ADMIT.CORE);
  const units = OPT.enumerateLegal(admitted, ctx.limits);
  if (!units) return { ...status, status: "RMA_LIMITS_UNVERIFIED", candidate: null, ladder: null };

  const cur = priceAndReplay(block, block.allocation, block.coverage_level, ctx);
  if (cur.error) return { ...status, status: "CURRENT_UNPRICEABLE", candidate: null, ladder: null };
  attachSeverity(cur, ctx);

  const universe = [];
  for (const u of units) {
    const alloc = {};
    admitted.forEach((l, i) => { if (u[i] > 0) alloc[l] = u[i] / 10; });
    const r = priceAndReplay(block, alloc, block.coverage_level, ctx);
    if (r.error) continue;
    r.net_per_dollar = r.protection ? r.avg_net / r.protection : null;
    attachSeverity(r, ctx);
    r.severe_net_per_dollar = r.protection ? r.severe_avg_net / r.protection : null;
    if (Number.isFinite(r.severe_net_per_dollar) && Number.isFinite(r.net_per_dollar)) universe.push(r);
  }
  if (!universe.length) return { ...status, status: "NO_UNIVERSE", candidate: null, ladder: null };

  const frontier = OPT.severityFrontier(universe);
  const maxEcon = Math.max(...frontier.map((f) => f.net_per_dollar));
  const ladder = [0.80, 0.85, 0.90, 0.95].map((r) => {
    const c = OPT.selectDroughtCandidate(frontier, cur, r);
    return c
      ? { retention: r, label: c.label, allocation: { ...c.allocation },
          severe_avg_net: c.severe_avg_net, severe_pay_freq: c.severe_pay_freq,
          pct_of_max_econ: c.net_per_dollar / maxEcon }
      : { retention: r, label: null, status: "NO_MATERIAL_IMPROVEMENT" };
  });
  const pick = OPT.selectDroughtCandidate(frontier, cur, retention);
  return {
    ...status,
    status: pick ? "OK" : "NO_MATERIAL_IMPROVEMENT",
    universe_size: universe.length, frontier_size: frontier.length,
    retention_used: retention, retention_is_provisional: true,
    materiality: OPT.MATERIALITY,
    materiality_bar_dollars: OPT.MATERIALITY.severeNetGainVsPremium * (cur.producer_premium || 0),
    ladder, ladder_distinct: new Set(ladder.filter((l) => l.label).map((l) => l.label)).size,
    candidate: pick ? {
      label: pick.label, allocation: { ...pick.allocation }, coverage: pick.coverage,
      protection: pick.protection, producer_premium: pick.producer_premium,
      avg_net: pick.avg_net, severe_avg_net: pick.severe_avg_net, severe_pay_freq: pick.severe_pay_freq,
      pct_of_max_econ: pick.net_per_dollar / maxEcon,
      relevance: OPT.allocationClasses(pick.allocation, ctx.stage1),
    } : null,
    current_severe: { avg_net: cur.severe_avg_net, pay_freq: cur.severe_pay_freq },
  };
}
function attachSeverity(r, ctx) {
  const m = OPT.severityMetrics(r.yearly, ctx.severity, ctx.years, r.protection, r.producer_premium);
  r.severity = m;
  r.severe_avg_net = m?.SEVERE?.avg_net ?? -Infinity;
  r.severe_pay_freq = m?.SEVERE?.pay_freq ?? 0;
  return r;
}

// ── the whole review for one block ─────────────────────────────────
function buildBlockReview(block, refs) {
  const _iv = indexSeries(refs.indexRows);
  // Extend with any crop year RMA has already settled but the vintage cannot
  // carry — see mergeSettledYears. Done HERE so every section (the replay, the
  // coverage ladder, interval history and the matrix) sees ONE series; two
  // would disagree about the newest year.
  const { series, years, settled_years } = mergeSettledYears(_iv.series, _iv.years, refs.settledRows || []);
  // Resolve the value RMA actually used, when the caller can supply the
  // actuarial table and the AIP's own records. A caller that cannot gets the
  // legacy `refs.cbv` and is told so on the returned object — it is never
  // silently substituted.
  let cbvRes = null;
  if (refs.cbvIndex) {
    try {
      cbvRes = ACT.resolveCbv(
        { ...block, stored_liability_for_solve: refs.solveLiability ?? null,
          solve_percent: refs.solvePercent ?? null },
        { cropYear: refs.cropYear, policyNumber: refs.policyNumber, stateCode: refs.stateCode,
          cbvIndex: refs.cbvIndex, fpiIndex: refs.fpiIndex || null, tolerance: 0.51,
          blockUse: block.intended_use || null, blockIntervals: block.intervals });
    } catch (_) { cbvRes = null; }
  }
  block = effectiveBlock(block, cbvRes);
  const { ctx, cbvForPricing, severity } = blockContext(block, refs, cbvRes, series, years);
  const current = priceAndReplay(block, block.allocation, block.coverage_level, ctx);
  return {
    block, ctx_ok: !current.error,
    // What the price is built on, and where that came from. A surface showing a
    // dollar figure can say whether it is RMA's own value or a fallback.
    // ⚠ A RESOLUTION THAT SOLVED NOTHING MUST NOT REPORT A BLANK WHILE THE
    // PRICE USES A FIGURE. resolveCbv works backwards from the AIP's own
    // liability, so for a grid with no policy behind it it returns an object
    // that is present but empty — and the screen then read "county base value —"
    // beside a protection figure computed on $447. Where the caller has STATED
    // the value (a human picked the practice), that is what is reported, marked
    // CALLER_SUPPLIED so nobody mistakes it for a solved one.
    //
    // A policy block passes no `refs.cbv`, so it takes the solved branch exactly
    // as before, whether or not the solve succeeded.
    cbv_resolution: (cbvRes && (cbvRes.ok || refs.cbv == null))
      ? { ok: cbvRes.ok, source: cbvRes.source || null, status: cbvRes.status || null,
          detail: cbvRes.detail || null, cbv: cbvRes.cbv ?? null,
          intended_use: cbvRes.use || block.intended_use || null,
          county: cbvRes.county || null,
          is_dollar_rounded: !!cbvRes.protection_is_dollar_rounded }
      : { ok: refs.cbv != null, source: "CALLER_SUPPLIED", cbv: refs.cbv ?? null,
          intended_use: block.intended_use || null, county: refs.countyCode || null,
          is_dollar_rounded: false },
    limits_status: ctx.limits ? "OK" : "RMA_LIMITS_UNVERIFIED",
    severity_available: !!severity,
    severity_counts: severity ? severity.counts : null,
    severity_years: severity ? severity.rows.filter((r) => r.klass === "SEVERE").map((r) => r.year) : null,
    current,
    // The stored figure is shown beside ours rather than replaced by it: a
    // disagreement with the AIP's own liability is a finding, not a rounding.
    stored: { liability: block.stored_liability, premium: block.stored_premium },
    coverage: current.error ? null : coverageSection(block, ctx),
    intervals: intervalSection(block, ctx),
    optimizer: optimizerSection(block, ctx),
    simulate: (alloc, cov) => simulate(block, alloc, cov, ctx),
    // What a caller needs to re-run the SAME replay over a chosen window and a
    // chosen coverage level, without rebuilding this context or growing a second
    // one beside it. The Interval History screen drives its period and coverage
    // selectors off exactly these — so the numbers it shows and the numbers the
    // rest of this object shows come from one place.
    history_inputs: {
      cbv: ctx.cbv, series: ctx.series, years: ctx.years,
      // Which of those years came from RMA's SETTLED index rather than from the
      // published vintage. The screen NAMES them; it never blends the two
      // silently, because the two are computed against different normals.
      settled_years,
      rateRows: refs.rateRows || [], typeCode: ctx.typeCode || null,
      countyCode: ctx.countyCode || null, practiceCode: ctx.practiceCode || null,
      // The FULL severity classification, not just the severe years. A caller
      // re-running the replay at another crop year needs SEVERE *and* NORMAL to
      // reproduce the same two history columns; rebuilding it from
      // `severity_years` alone silently drops the NORMAL column to blanks,
      // which reads as "no normal years on this grid" rather than as a caller
      // that could not classify them.
      severity: ctx.severity || null,
      limits: ctx.limits || null,
      acres: Number(block.acres) || 0,
      productivity_factor: Number(block.productivity_factor) || null,
      allocation: block.allocation || {},
    },
  };
}

// ── GRID EXPLORER — a grid, with no policy and no quote ────────────
//
// An agent clicks a grid on the map and wants its published behaviour: 77 years
// of interval history, what RMA charges there now, and what a trigger has
// historically looked like. No customer, no acres, no application.
//
// ⚠ IT PRODUCES NO DOLLARS, AND THAT IS A DESIGN CONSTRAINT RATHER THAN AN
// OMISSION. A dollar figure needs acres, a productivity factor, a share and a
// percent of value — every one of which is a producer's ELECTION, not a
// property of the grid. Inventing defaults for them so the screen could show a
// premium would put a number in front of an agent that no producer chose, and
// it would look exactly like a quote. What the grid itself determines is the
// RATE and the county base value; those are shown, and the quote is a separate,
// deliberate act.
//
// The interval table comes from `intervalSection` — the SAME builder the policy
// view uses — over a VIRTUAL block carrying only a coverage level. Two copies of
// the 77-year math is how the explorer and the policy screen come to disagree
// about the same grid.
function buildGridExplorer(refs) {
  const _iv = indexSeries(refs.indexRows);
  // Extend with any crop year RMA has already settled but the vintage cannot
  // carry — see mergeSettledYears. Done HERE so every section (the replay, the
  // coverage ladder, interval history and the matrix) sees ONE series; two
  // would disagree about the newest year.
  const { series, years, settled_years } = mergeSettledYears(_iv.series, _iv.years, refs.settledRows || []);
  const coverage = Number(refs.coverageLevel) || 0.85;
  const stage1 = OPT.stage1();
  let severity = null;
  try { severity = OPT.severityByYear(series, years, stage1.core); } catch (_) { severity = null; }

  // No acres, no allocation, no liability: the block exists only to carry the
  // coverage level into the shared builder. `in_current` is false for every
  // interval because there is no current policy, which is the truth here.
  const block = {
    key: `explorer|${refs.grid}`, grid: Number(refs.grid), county: refs.county || null,
    acres: null, coverage_level: coverage, productivity_factor: null,
    allocation: {}, intervals: LAB.slice(), intended_use: null,
    stored_liability: null, stored_premium: null,
  };
  const ctx = {
    series, years, stage1, severity, severityDefId: "MEAN_CORE_P20",
    cbv: null, rateRows: refs.rateRows || [],
    typeCode: null, countyCode: null, practiceCode: null,
    limits: limitsFor(refs.limitRows, refs.grid, refs.cropYear),
    sourceCropYear: refs.sourceCropYear ?? null,
  };

  // What RMA publishes for this grid, PER INTENDED USE and PER COUNTY. A grid
  // is a 0.25° box and boxes straddle county lines, so one grid can carry
  // several counties — each with its own county base value. They are listed,
  // never averaged: a mean of two counties' values is a number RMA never
  // published and that no policy could ever be written at.
  const byUse = {};
  for (const c of (refs.cbvRows || [])) {
    if (Number(c.grid_id) !== Number(refs.grid)) continue;
    if (refs.cropYear != null && Number(c.crop_year) !== Number(refs.cropYear)) continue;
    const use = ACT.INTENDED_USE[c.type_code] || c.type_code || "unknown";
    const k = `${use}|${c.state_code}|${c.county_code}`;
    (byUse[k] ||= { use, type_code: c.type_code, state_code: c.state_code,
                    county_code: c.county_code, byPractice: new Map() });
    byUse[k].byPractice.set(String(c.practice_code), Number(c.county_base_value));
  }
  const cbv_by_use = Object.values(byUse).map((r) => {
    const vals = [...new Set(r.byPractice.values())];
    // ⚠ HAYING IS GENUINELY PRACTICE-DEPENDENT HERE AND MUST NOT COLLAPSE TO ONE
    // NUMBER. Measured on grid 27509 CY2026: grazing publishes ONE value across
    // its 11 practices ($67.30) while haying publishes several across 66. That
    // is why resolveCbv has to solve haying from the AIP's own liability rather
    // than read it off the table — and it is a fact about RMA's actuarials, not
    // a defect in ours.
    //
    // A QUOTE still refuses an ambiguous cell. An EXPLORER prices nothing, so it
    // shows the published SET with the practice each value belongs to: an agent
    // asking "what is haying worth in this grid" is better served by the real
    // spread than by a refusal, and better by a refusal than by a mean nobody
    // published.
    const spread = [...r.byPractice.entries()]
      .map(([practice_code, cbv]) => ({ practice_code, cbv }))
      .sort((a, b) => a.cbv - b.cbv || String(a.practice_code).localeCompare(String(b.practice_code)));
    return {
      use: r.use, type_code: r.type_code, state_code: r.state_code, county_code: r.county_code,
      practices: r.byPractice.size,
      // Codes RMA publishes that we have no established meaning for render RAW,
      // the same convention the claim stage codes and corn type codes follow.
      use_is_named: r.use !== r.type_code,
      cbv: vals.length === 1 ? vals[0] : null,
      values: spread,
      min_cbv: vals.length ? Math.min(...vals) : null,
      max_cbv: vals.length ? Math.max(...vals) : null,
      status: vals.length === 1 ? "PUBLISHED" : "VARIES_BY_PRACTICE",
      detail: vals.length === 1 ? null
        : `${r.byPractice.size} practices publish ${vals.length} different values ($${Math.min(...vals)}–$${Math.max(...vals)})`,
    };
  }).sort((a, b) => String(a.use).localeCompare(String(b.use)) || String(a.county_code).localeCompare(String(b.county_code)));

  // ⚠ A PREMIUM RATE IS PER COUNTY, SO IT NEEDS ONE. A grid is a 0.25° box and
  // spans several counties — 27509 covers four — and pooling them lets a rate
  // from one county sit beside the county base value of another. `ratesFor`
  // could not catch it: measured on CY2026, 62,035 of the cells where a type is
  // missing hold ONE rate anyway, so a pooled lookup returns something entirely
  // plausible. The counties are listed and one is chosen, never merged.
  //
  // The rate itself is a rainfall-risk figure and does NOT vary by intended use
  // — measured, not assumed: of 100,776 CY2026 (grid, county, interval,
  // coverage) cells, 42,641 publish only ONE of haying/grazing, and where both
  // appear they agree. That is why one rate column is honest and two identical
  // ones were not: printing them side by side implied a distinction RMA does
  // not make.
  const counties = [...new Set((refs.rateRows || []).map((r) => r.county_code).filter((c) => c != null))].sort();
  const county = refs.countyCode != null && counties.includes(String(refs.countyCode))
    ? String(refs.countyCode) : (counties[0] ?? null);
  const countyRows = county == null ? (refs.rateRows || [])
    : (refs.rateRows || []).filter((r) => String(r.county_code) === county);
  const rateTypes = [...new Set(countyRows.map((r) => r.type_code))].filter((t) => t != null);
  const rates = ratesFor(countyRows, coverage, null);
  const rates_by_use = [{
    use: "ALL USES", type_code: null, rates,
    ambiguous: rates.__ambiguous || [],
    // Named so a screen can say which types actually published here rather than
    // implying the rate was looked up per use.
    types_present: rateTypes,
  }];

  return {
    grid: Number(refs.grid), crop_year: refs.cropYear ?? null, coverage_level: coverage,
    // Which county the rates above belong to, and the others this grid spans.
    rate_county: county, rate_counties: counties,
    record: {
      years: years.length,
      settled_years,
      first_year: years[0] ?? null,
      last_year: years[years.length - 1] ?? null,
      // A grid with no loaded history is a NAMED absence, never a zero-year
      // record rendered as "never triggered".
      available: years.length > 0,
    },
    intervals: intervalSection(block, ctx),
    cbv_by_use, rates_by_use,
    limits_status: ctx.limits ? "OK" : "RMA_LIMITS_UNVERIFIED",
    severity_available: !!severity,
    severity_counts: severity ? severity.counts : null,
    // Everything a "Start quote in this grid" action needs to hand over, so the
    // quote opens on what the agent was just looking at rather than on defaults.
    quote_seed: { grid: Number(refs.grid), crop_year: refs.cropYear ?? null, coverage_level: coverage },
  };
}

module.exports = {
  COVERAGE_LEVELS, LAB, PRF_INTERVALS,
  buildGridExplorer,
  // re-exported so a UI caller needs one import, not two
  indexCbv: ACT.indexCbv, indexFieldPrf: ACT.indexFieldPrf,
  effectiveBlock, billedTotals,
  // THE USE->TYPE MAP IS RE-EXPORTED SO NOBODY WRITES A THIRD COPY OF IT.
  // App.jsx carried the mapping twice as an inline ternary, and one of them was
  // `use === "GRAZING" ? "007" : "030"` - which silently prices ANY unstated use
  // as HAYING. Haying runs about five times grazing, so that default is a wrong
  // quote that looks right. `USE_TO_TYPE[use]` is undefined for anything it does
  // not know, which is the fail-closed answer.
  INTENDED_USE: ACT.INTENDED_USE, USE_TO_TYPE: ACT.USE_TO_TYPE, resolveCbv: ACT.resolveCbv,
  prfBlocks, indexSeries, mergeSettledYears, ratesFor, limitsFor, checkAllocation,
  policyOverview, blockContext,
  priceAndReplay, coverageSection, intervalSection, simulate, optimizerSection,
  buildBlockReview,
};
